import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography} from 'views/components';
import {Translate} from 'views/module';

const SlipeTimeline = (props) => {
  const {index, date, title, shiftPct, currentShift} = props;

  return (
    <Box key={index} sx={{mb: 3}}>
      <Typography sx={{fontWeight: 'bold', mb: 2}}>
        <Translate
          expression='caregiver.CaregiverDetail.Payment.WorkSummary.HoursWorked.ShiftHeader'
          substitute={{date, title}}
        />
      </Typography>
      {
        (currentShift.holidayMultiplier || 1) !== 1 &&
        <Typography sx={{color: '#b26a00', fontWeight: 600, fontSize: '0.8rem', mb: 2, mt: -1}}>
          {
            currentShift.holidayName ?
            <Translate
              expression='caregiver.CaregiverDetail.Payment.StatHolidayNamed'
              substitute={{name: currentShift.holidayName}}
            /> :
            <Translate expression='caregiver.CaregiverDetail.Payment.StatHoliday' />
          }
        </Typography>
      }

      {/* Individual shift timeline */}
      <Box sx={{position: 'relative', mb: 3}}>
        {/* Clock in and out times above timeline */}
        <Box sx={{position: 'relative', width: '100%', mb: 1, height: '20px'}}>
          {/* Actual clock-out time above (only if exists) */}
          {
            currentShift.actualOut &&
            <Typography
              sx={{
                position: 'absolute',
                left: `${shiftPct.percent(currentShift.actualOut) - 2}%`,
                fontSize: '0.75rem',
                color: '#ff6b35',
                fontWeight: 'bold',
              }}
            >
              {currentShift.actualOut.format('h:mm A')}
            </Typography>
          }
          {/* Actual clock-in time above (only if exists) */}
          {
            currentShift.actualIn &&
            <Typography
              sx={{
                position: 'absolute',
                left: `${shiftPct.percent(currentShift.actualIn) - 2}%`,
                fontSize: '0.7rem',
                color: '#2196f3',
                fontWeight: 'bold',
              }}
            >
              {currentShift.actualIn.format('h:mm A')}
            </Typography>
          }
        </Box>

        <Box sx={{width: '100%', position: 'relative', height: 16, background: '#f5f5f5', borderRadius: '8px'}}>
          {/* Scheduled time bar*/}
          <Box
            sx={{
              position: 'absolute',
              left: `${shiftPct.percent(currentShift.scheduledStart)}%`,
              width: `${shiftPct.percent(currentShift.scheduledEnd) - shiftPct.percent(currentShift.scheduledStart)}%`,
              height: '100%',
              background: '#81c784',
              borderRadius: '8px',
              opacity: 0.7,
            }}
          />
          {/* Actual clock-in (blue)*/}
          {
            currentShift.actualIn &&
            <Box
              sx={{
                position: 'absolute',
                left: `${shiftPct.percent(currentShift.actualIn)}%`,
                width: 14,
                height: 14,
                background: '#2196f3',
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            />
          }
          {/* Actual clock-out (Orange)*/}
          {
            currentShift.actualOut &&
            <Box
              sx={{
                position: 'absolute',
                left: `${shiftPct.percent(currentShift.actualOut) - 1}%`,
                width: 14,
                height: 14,
                background: '#ff6b35',
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
              }}
            />
          }
        </Box>

        {/* Time labels under timeline - scheduled times only */}
        <Box sx={{position: 'relative', width: '100%', mt: 1, height: '20px'}}>
          {/* Edge time - Start of period */}
          <Typography
            sx={{
              position: 'absolute',
              fontSize: '0.65rem',
              color: 'text.disabled',
              fontStyle: 'italic',
            }}
          >
            {shiftPct.minTime.format('h:mm A')}
          </Typography>

          {/* Scheduled start time */}
          <Typography
            sx={{
              position: 'absolute',
              left: `${shiftPct.percent(currentShift.scheduledStart) - 2}%`,
              fontSize: '0.7rem',
              color: '#4caf50',
              fontWeight: 'medium',
            }}
          >
            {currentShift.scheduledStart.format('h:mm A')}
          </Typography>

          {/* Scheduled end time */}
          <Typography
            sx={{
              position: 'absolute',
              left: `${shiftPct.percent(currentShift.scheduledEnd) - 2}%`,
              fontSize: '0.7rem',
              color: '#4caf50',
              fontWeight: 'medium',
            }}
          >
            {currentShift.scheduledEnd.format('h:mm A')}
          </Typography>

          {/* Edge time - End of period */}
          <Typography
            sx={{
              position: 'absolute',
              left: '96%',
              fontSize: '0.65rem',
              color: 'text.disabled',
              fontStyle: 'italic',
              textWrap: 'nowrap',
            }}
          >
            {shiftPct.maxTime.format('h:mm A')}
          </Typography>
        </Box>
        {/* Clock in/out labels below bar */}
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1}}>
          <Box>
            <Typography sx={{fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4}}>
              <Translate expression='caregiver.CaregiverDetail.Payment.WorkSummary.HoursWorked.ClockIn'/>
            </Typography>
            <Typography sx={{fontWeight: 600, color: '#2196f3'}}>
              {currentShift.actualIn ? currentShift.actualIn.format('h:mm A') : '--'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4}}>
              <Translate expression='caregiver.CaregiverDetail.Payment.WorkSummary.HoursWorked.ClockOut'/>
            </Typography>
            <Typography sx={{fontWeight: 600, color: '#ff6b35'}}>
              {currentShift.actualOut ? currentShift.actualOut.format('h:mm A') : '--'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

SlipeTimeline.propTypes = {
  index: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  shiftPct: PropTypes.object.isRequired,
  currentShift: PropTypes.object.isRequired,
};

export default React.memo(SlipeTimeline);
